import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  Input,
  ReportTable,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { PermissionSlug } from '../constants/featureFlags';
import {
  exportWorklogReportReportsWorklogExportGet,
  getWorklogReportOptionsReportsWorklogOptionsGet,
  getWorklogReportReportsWorklogGet,
} from '../services/api/sdk.gen';
import { SORTORDER_VALUES } from '../services/api/models/SortOrder';
import { WORKLOGSORTBY_VALUES } from '../services/api/models/WorklogSortBy';
import type {
  WorklogOption,
  WorklogReportResult,
  WorklogReportRow,
} from '../services/api/types.gen';
import {
  formatDateToISO,
  formatDateTimeInAgencyZone,
  getBusinessToday,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import {
  coerceReportPageSize,
  formatReportDate,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_EMPTY_VALUE,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import { createReportTextColumn } from '../utils/reportColumns';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { buildReportPopulationQuery } from '../components/reporting/reportPopulation';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

const ALL_USERS_VALUE = 'all';

// Times read in the agency's zone, as they do in the visit work log dialog.
const dateTimeDash = (value: string | null | undefined) =>
  formatDateTimeInAgencyZone(value) || REPORT_EMPTY_VALUE;

const textColumn = createReportTextColumn<WorklogReportRow>;

const columns: ReportTableColumn<WorklogReportRow>[] = [
  textColumn(
    'patient_last_name',
    'Patient Last Name',
    'patient_last_name',
    150
  ),
  textColumn(
    'patient_first_name',
    'Patient First Name',
    'patient_first_name',
    150
  ),
  textColumn('clinic', 'Clinic', 'clinic', 180),
  {
    ...textColumn(
      'episode_start_date',
      'Episode Start Date',
      'episode_start_date',
      140
    ),
    accessor: row => formatReportDate(row.episode_start_date),
  },
  textColumn('task_name', 'Task Name', 'task_name', 220),
  {
    ...textColumn(
      'task_target_date',
      'Task Target Date',
      'task_target_date',
      140
    ),
    accessor: row => formatReportDate(row.task_target_date),
  },
  textColumn('action_taken', 'Action Taken', 'action_taken', 180),
  {
    ...textColumn(
      'date_action_taken',
      'Date Action Taken',
      'date_action_taken',
      190
    ),
    accessor: row => dateTimeDash(row.date_action_taken),
  },
  textColumn('username', 'UserName', 'username', 180),
];

function defaultDateRange() {
  const to = getBusinessToday();
  const fromDate = parseISOToLocalDate(to);
  if (!fromDate) return { from: to, to };
  fromDate.setDate(fromDate.getDate() - 7);
  return { from: formatDateToISO(fromDate), to };
}

const WorklogReport = () => {
  const { canViewReports, canViewWorklog } = useStore(
    useShallow(state => ({
      canViewReports: state.hasPermission(
        PermissionSlug.ROGER_EMR_REPORTS_ACCESS
      ),
      canViewWorklog: state.hasPermission(PermissionSlug.WORKLOG_VIEW),
    }))
  );
  // The worklog report needs the reports gate and the work log gate together.
  const canViewReport = canViewReports && canViewWorklog;
  const reportsBackHref = useReportsBackHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = useMemo(() => defaultDateRange(), []);
  const [options, setOptions] = useState<WorklogOption[]>([]);
  const [report, setReport] = useState<WorklogReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '');

  const hasUrlDateRange = searchParams.has('from') || searchParams.has('to');
  const dateFrom =
    searchParams.get('from') ?? (hasUrlDateRange ? '' : defaults.from);
  const dateTo = searchParams.get('to') ?? (hasUrlDateRange ? '' : defaults.to);
  const userId = searchParams.get('user') ?? '';
  const q = searchParams.get('q') ?? '';
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const includeInactiveEpisodes =
    searchParams.get('include_inactive_episodes') === 'true';
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = coerceReportPageSize(Number(searchParams.get('size')));
  const sortBy = readOptionalChoiceParam(
    searchParams.get('sort'),
    WORKLOGSORTBY_VALUES
  );
  const sortOrder = sortBy
    ? readOptionalChoiceParam(searchParams.get('dir'), SORTORDER_VALUES)
    : undefined;
  const hasIncompleteRange = !dateFrom || !dateTo;

  const updateParams = useCallback(
    (updates: Record<string, string | null>, resetPage = true) => {
      setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        });
        if (resetPage) {
          next.delete('page');
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const updateDateParam = useCallback(
    (field: 'from' | 'to', value: string | null) => {
      const updates: Record<string, string | null> = { [field]: value };
      if (!hasUrlDateRange) {
        updates[field === 'from' ? 'to' : 'from'] =
          field === 'from' ? defaults.to : defaults.from;
      }
      updateParams(updates);
    },
    [defaults.from, defaults.to, hasUrlDateRange, updateParams]
  );

  useEffect(() => {
    setSearchText(searchParams.get('q') ?? '');
  }, [searchParams]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (searchText !== q) {
        updateParams({ q: searchText || null });
      }
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [q, searchText, updateParams]);

  useEffect(() => {
    if (!canViewReport) {
      return;
    }
    let cancelled = false;
    if (hasIncompleteRange) {
      setOptions([]);
      return;
    }
    void getWorklogReportOptionsReportsWorklogOptionsGet({
      query: {
        date_from: dateFrom,
        date_to: dateTo,
        ...buildReportPopulationQuery({
          includeTestRows,
          includeInactiveEpisodes,
        }),
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error(
            'Failed to load worklog report options',
            response.error
          );
          setOptions([]);
          return;
        }
        setOptions(response.data.users ?? []);
      })
      .catch(error => {
        if (!cancelled) {
          console.error('Failed to load worklog report options', error);
          setOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    canViewReport,
    dateFrom,
    dateTo,
    hasIncompleteRange,
    includeInactiveEpisodes,
    includeTestRows,
  ]);

  useEffect(() => {
    if (!canViewReport) {
      return;
    }
    if (hasIncompleteRange) {
      setIsLoading(false);
      setReport(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    void getWorklogReportReportsWorklogGet({
      query: {
        date_from: dateFrom,
        date_to: dateTo,
        users: userId || undefined,
        q: q || undefined,
        ...buildReportPopulationQuery({
          includeTestRows,
          includeInactiveEpisodes,
        }),
        sort_by: sortBy,
        sort_direction: sortOrder,
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError(
            getBusinessErrorMessage({
              response: {
                status: response.response?.status,
                data: response.error,
              },
            }) ?? 'Unable to load worklog report. Please try again.'
          );
          setReport(null);
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        if (!cancelled) {
          console.error('Failed to load worklog report', error);
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load worklog report. Please try again.'
          );
          setReport(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    canViewReport,
    dateFrom,
    dateTo,
    hasIncompleteRange,
    includeInactiveEpisodes,
    includeTestRows,
    page,
    pageSize,
    q,
    sortBy,
    sortOrder,
    userId,
  ]);

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortOrder ?? 'asc' }
    : { id: 'date_action_taken', direction: 'asc' };

  const pagination: ReportTablePaginationState = {
    page,
    pageSize,
  };

  const emptyState = hasIncompleteRange ? (
    <EmptyState
      title='Choose a date range'
      description='Choose From and To dates to load the worklog report.'
    />
  ) : (
    <EmptyState
      title='No worklog activity'
      description='No worklog rows match the selected filters.'
    />
  );

  const download = async () => {
    if (hasIncompleteRange) {
      return;
    }
    try {
      const response = await exportWorklogReportReportsWorklogExportGet({
        query: {
          date_from: dateFrom,
          date_to: dateTo,
          users: userId || undefined,
          q: q || undefined,
          ...buildReportPopulationQuery({
            includeTestRows,
            includeInactiveEpisodes,
          }),
          sort_by: sortBy,
          sort_direction: sortOrder,
        },
      });
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download worklog report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response as never, 'worklog-report.xlsx');
    } catch (error) {
      console.error('Failed to download worklog report', error);
      setError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download worklog report. Please try again.'
        )
      );
    }
  };

  if (!canViewReport) {
    return <ReportsPermissionAlert />;
  }

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start gap-3'>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download()}
            disabled={isLoading || hasIncompleteRange}
          >
            <Download className='h-4 w-4' /> Export
          </Button>
          <ReportPopulationControls
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? 'true' : null,
              })
            }
          />
        </div>
      </div>

      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='grid gap-3 md:grid-cols-4'>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='worklog-date-from'>From</label>
            <DatePicker
              id='worklog-date-from'
              date={parseISOToLocalDate(dateFrom)}
              datePickerSize='default'
              onDateChange={date =>
                updateDateParam('from', date ? formatDateToISO(date) : null)
              }
              showClearButton
            />
          </div>
          <div className='space-y-1 text-sm font-medium'>
            <label htmlFor='worklog-date-to'>To</label>
            <DatePicker
              id='worklog-date-to'
              date={parseISOToLocalDate(dateTo)}
              datePickerSize='default'
              onDateChange={date =>
                updateDateParam('to', date ? formatDateToISO(date) : null)
              }
              showClearButton
            />
          </div>
          <label
            className='space-y-1 text-sm font-medium'
            htmlFor='worklog-user-filter'
          >
            User
            <Select
              value={userId || ALL_USERS_VALUE}
              onValueChange={value =>
                updateParams({ user: value === ALL_USERS_VALUE ? null : value })
              }
            >
              <SelectTrigger id='worklog-user-filter' selectSize='default'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_USERS_VALUE}>All users</SelectItem>
                {options.map(option => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className='space-y-1 text-sm font-medium'>
            Search
            <Input
              inputSize='default'
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
              placeholder='Patient, clinic, task, action, or user'
            />
          </label>
        </div>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='Worklog report results'
        sorting={sorting}
        onSortingChange={next => {
          updateParams(
            next
              ? { sort: next.id, dir: next.direction }
              : { sort: null, dir: null }
          );
        }}
        isLoading={isLoading}
        emptyState={emptyState}
        pagination={{
          ...pagination,
          total: report?.total ?? 0,
          totalPages: Math.ceil((report?.total ?? 0) / pageSize),
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
        }}
        onPaginationChange={next => {
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          });
        }}
      />
    </div>
  );
};

export default WorklogReport;
